import { computed, reactive } from 'vue'
import { freezeObject, isFunction } from '@/global/utils/utils'
import { dateService } from '@/global/services/date'

export function useCreate (options) {
  const {
    router,
    trans,
    columnsContentComponent,
    viewDatetimeFormat,
    sqlDatetimeFormat,
    projectTypes,
    closeGeolocationsDialogFn,
    openGeolocationsDialogFn,
    tabActionsComponent,
    datePickerFormat,
    onCancelCreate
  } = options || {}

  const dateEndConfig = reactive({
    minDate: null
  })
  const dateStartConfig = reactive({
    maxDate: null
  })

  const apiConfig = freezeObject({
    post: {
      module: 'road-maintenance',
      route: 'projects'
    }
  })

  const onLocationSaveCallback = (event) => {
    const { updateData } = event?.event || {}
    if (!isFunction(updateData)) {
      return
    }

    const { address, coordinates } = event || {}
    updateData({
      address,
      latitude: coordinates?.lat || null,
      longitude: coordinates?.lng || null
    })

    closeGeolocationsDialogFn()
  }

  const onOpenGeolocationDialog = (event) => {
    if (!isFunction(openGeolocationsDialogFn)) {
      return
    }

    const { latitude, longitude } = event?.formData || {}

    const dialogOptions = {
      title: trans('base.location_selection'),
      positiveButtonText: trans('base.confirm'),
      negativeButtonText: trans('base.cancel'),
      georeverseAddress: true,
      createMode: true,
      event,
      onConfirm: onLocationSaveCallback
    }

    if (latitude && longitude) {
      dialogOptions.markers = {
        lat: latitude,
        lng: longitude
      }
    }

    openGeolocationsDialogFn(dialogOptions)
  }

  const modifyDataBeforeSend = (event) => {
    const { copiedData } = event || {}
    copiedData.date_start = copiedData?.date_start ? dateService.formatCustom(copiedData.date_start, viewDatetimeFormat, sqlDatetimeFormat) : null
    copiedData.date_end = copiedData?.date_end ? dateService.formatCustom(copiedData.date_end, viewDatetimeFormat, sqlDatetimeFormat) : null
    return copiedData
  }

  const redirectToEdit = (event, data) => {
    const { id } = data || {}
    if (!id) {
      return
    }

    router.push({
      name: 'TemporaryRegulationsWorkOrderDetails',
      params: {
        id
      }
    })
  }

  const onCancelTabSave = (event) => {
    const { cancelTabSave } = event || {}

    if (isFunction(cancelTabSave)) {
      cancelTabSave()
    }

    if (isFunction(onCancelCreate)) {
      onCancelCreate()
    }
  }

  const tabActions = computed(() => ({
    component: tabActionsComponent,
    title: trans('fleet/vehicle.basic_info'),
    showEditButtons: true,
    onCancelTabSave
  }))

  const fields = computed(() => ([
    {
      key: 'type_id',
      label: trans('road-maintenance/projects.project_type'),
      type: 'autocomplete',
      items: projectTypes.value,
      autocomplete_options: {
        custom_display_value: (value) => {
          return value?.description ? trans(value.description) : ''
        }
      },
      creatable: true,
      editable: true,
      visible: true
    },
    {
      key: 'date_start',
      label: trans('road-maintenance/work_orders.start_date'),
      type: 'datetime-picker',
      appendIcon: 'mdi-calendar-month',
      format: viewDatetimeFormat,
      placeholder: viewDatetimeFormat,
      creatable: true,
      editable: true,
      visible: true,
      maxDate: dateStartConfig.maxDate,
      onInput: (value, event) => {
        const { updateValidationErrors, formData } = event || {}
        const { date_end: dateEnd } = formData || {}
        if (isFunction(updateValidationErrors)) {
          if (value) {
            if (!dateService.isValid(value, viewDatetimeFormat, true)) {
              updateValidationErrors({
                date_start: trans('base.invalid_date').replace(':format', viewDatetimeFormat)
              })
            }
            else if (dateEnd && dateService.isBefore(dateEnd, value, viewDatetimeFormat)) {
              updateValidationErrors({
                date_start: trans('base.start_date_less_than_end_date_message')
              })
            }
            else {
              dateEndConfig.minDate = dateService.formatCustom(value, viewDatetimeFormat, datePickerFormat)
            }
          }
          else {
            dateEndConfig.minDate = null
          }
        }
      }
    },
    {
      key: 'date_end',
      label: trans('road-maintenance/work_orders.end_date'),
      type: 'datetime-picker',
      appendIcon: 'mdi-calendar-month',
      format: viewDatetimeFormat,
      placeholder: viewDatetimeFormat,
      creatable: true,
      editable: true,
      visible: true,
      minDate: dateEndConfig.minDate,
      onInput: (value, event) => {
        const { updateValidationErrors, formData } = event || {}
        const { date_start: dateStart } = formData || {}
        if (isFunction(updateValidationErrors)) {
          if (value) {
            if (!dateService.isValid(value, viewDatetimeFormat, true)) {
              updateValidationErrors({
                date_end: trans('base.invalid_date').replace(':format', viewDatetimeFormat)
              })
            }
            else if (dateStart && dateService.isBefore(value, dateStart, viewDatetimeFormat)) {
              updateValidationErrors({
                date_end: trans('base.end_date_greater_than_start_date_message')
              })
            }
            else {
              dateStartConfig.maxDate = dateService.formatCustom(value, viewDatetimeFormat, datePickerFormat)
            }
          }
          else {
            dateStartConfig.maxDate = null
          }
        }
      }
    },
    {
      key: 'address',
      label: trans('road-maintenance/patrol-service.address'),
      appendIcon: 'mdi-map-marker',
      appendIconClick: onOpenGeolocationDialog,
      creatable: true,
      editable: true,
      visible: true
    }
  ]))

  return computed(() => ({
    containerStyle: {
      padding: 0
    },
    tabs: [
      {
        title: trans('fleet/vehicle.basic_info'),
        apiConfig,
        modifyDataBeforeSend,
        afterSave: redirectToEdit,
        hiddenFormKeys: [
          'latitude',
          'longitude'
        ],
        tabContent: [
          tabActions.value,
          {
            component: columnsContentComponent,
            showButtons: false,
            rows: [
              {
                type: 'fields',
                columnsCount: 3,
                fields: fields.value
              }
            ]
          }
        ]
      }
    ]
  }))
}
